// ============================================================
// PLUMME — AI Rådgiver: status per anbefaling
// ============================================================
// Statusene her er brukerens egne notater om hva hen har gjort med et råd. De
// utfører ingenting — SuperPlumma setter ikke i gang noe på egen hånd, og det
// finnes derfor med vilje ingen status som påstår at den jobber. Eldre `handoff`
// ble lagret da chatten åpnet og betyr ikke at brukeren fullførte noe.
//
// Det vanskelige er ikke å lagre status, men å vite når den ikke gjelder lenger:
//  - «Fullført» på forfalte fakturaer i mars sier ingenting om nye forfalte i juni.
//  - «Utsatt til 17. juli» skal komme tilbake av seg selv den 17. juli.
// Begge deler håndteres her, slik at et gammelt hakeklikk ikke skjuler et nytt problem.
const PlummeAiAdvisorStatus = (() => {
  const STATUSES = ['new', 'handoff', 'snoozed', 'done', 'dismissed'];
  const DISMISS_REASONS = ['alreadyDone', 'planChanged', 'wrongAmount', 'notNow', 'notWanted'];
  // Hva statusen betyr for handlingssenteret på Oversikt.
  const BUCKET = { new: 'now', handoff: 'waiting', snoozed: 'planned', done: 'closed', dismissed: 'closed' };

  const safeArray = (value) => (Array.isArray(value) ? value : []);
  // Daily work requires identifiable records and a concrete review or correction.
  // Financial trends, calculated balances and calendar reminders remain insights.
  // Unknown recommendations stay out until their completion criteria are defined.
  const DAILY_TASK_IDS = new Set(['overdueSales', 'missingVouchers', 'unmatchedBank',
    'supplierDue', 'duplicatePurchase', 'draftInvoices', 'draftPurchases', 'inboxBacklog']);
  const isDailyTask = (rec) => rec?.lvl !== 'opp' && DAILY_TASK_IDS.has(rec?.id);
  const dailyTasks = (recommendations) => safeArray(recommendations).filter(isDailyTask);
  const startOfDay = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : new Date(date).setHours(0, 0, 0, 0);
  };

  // Fingeravtrykket er tallet rådet handlet om da brukeren tok stilling til det.
  // Endrer det seg, er det ikke lenger samme sak: et nytt forfalt beløp er et nytt
  // problem selv om anbefalingens id er den samme.
  const fingerprintOf = (rec) => `${rec?.id || ''}|${rec?.year ? `${rec.year}|` : ''}${rec?.impact ?? ''}${rec?.search ? `|${rec.search}` : ''}`;

  const resolveStatus = (stored, rec, now = new Date()) => {
    const fallback = { status: 'new', bucket: 'now', stale: false, note: null, snoozeUntil: null, at: null };
    if (!stored || !STATUSES.includes(stored.status) || stored.status === 'new') return fallback;
    // Opening chat used to save a handoff before the user approved any action.
    if (stored.status === 'handoff') return fallback;

    // Utsettelsen har gått ut — rådet skal opp igjen av seg selv.
    if (stored.status === 'snoozed') {
      const until = startOfDay(stored.snoozeUntil);
      const today = startOfDay(now);
      if (until == null || today == null || today >= until) {
        return { ...fallback, note: stored.note || null, at: stored.at || null, expired: true };
      }
    }

    // Situasjonen har endret seg siden brukeren tok stilling. Vi nullstiller ikke i
    // det stille: statusen faller tilbake til «ny», men flagges som foreldet så UI
    // kan si hvorfor den kom tilbake.
    const stale = Boolean(stored.fingerprint) && Boolean(rec) && stored.fingerprint !== fingerprintOf(rec);
    if (stale) return { ...fallback, stale: true, previous: stored.status, at: stored.at || null };

    return {
      status: stored.status,
      bucket: BUCKET[stored.status] || 'now',
      stale: false,
      note: stored.note || null,
      snoozeUntil: stored.snoozeUntil || null,
      at: stored.at || null,
    };
  };

  // Anbefalinger sortert i bøttene handlingssenteret viser. Rekkefølgen på bøttene
  // er fast; tomme bøtter filtreres bort av UI.
  const groupByBucket = (recommendations = [], statuses = {}, now = new Date()) => {
    const groups = { now: [], waiting: [], planned: [], closed: [] };
    safeArray(recommendations).forEach((rec) => {
      const resolved = resolveStatus(statuses[rec.id], rec, now);
      groups[resolved.bucket].push({ rec, resolved });
    });
    return groups;
  };
  const visibleNow = (groups, limit = Infinity) => safeArray(groups?.now).slice(0, limit);

  const isActionable = (rec, statuses = {}, now = new Date()) =>
    isDailyTask(rec) && resolveStatus(statuses[rec?.id], rec, now).bucket === 'now';

  // Hvilke anbefalinger som har en handling plattform-routeren kan utføre, og hva
  // rådgiveren skal be den om. Nøkkelen er i18n-nøkkelen for forespørselen — teksten
  // ligger i katalogene, ikke her, slik at engelsk UI ber på engelsk.
  //
  // `missingVouchers` åpner filtrerte kjøp direkte. `vatDue` og `costGrowth`
  // har heller ingen handling som routeren kan ta imot.
  const PLATFORM_ACTION = {
    overdueSales: { intent: 'send_payment_reminder', requestKey: 'adv.handoff.request.overdueSales' },
  };
  const platformActionFor = (recId) => PLATFORM_ACTION[recId] || null;

  return { STATUSES, DISMISS_REASONS, BUCKET, PLATFORM_ACTION, platformActionFor, fingerprintOf, resolveStatus, groupByBucket, visibleNow, isActionable, isDailyTask, dailyTasks };
})();

if (typeof globalThis !== 'undefined') globalThis.PlummeAiAdvisorStatus = PlummeAiAdvisorStatus;
if (typeof module !== 'undefined' && module.exports) module.exports = PlummeAiAdvisorStatus;
